import { useEffect, useState } from 'react';

// Coming back to the tab is the person re-engaging, so a dead socket reconnects without the
// Reconnect prompt. One try per return: if it fails, the prompt shows, so a server outage
// stays visible instead of being retried behind their back.
export const RECONNECT_DELAY_MS = 500;
// A socket killed while hidden can report its close only after the tab is back (mobile
// browsers freeze the page), so a drop this soon after returning still counts.
export const RETURN_WINDOW_MS = 5000;
// The status stays 'stale' until the new socket opens; past this, the try has failed.
export const HOLD_MS = 5000;
// A laptop waking from sleep is often still rejoining Wi-Fi: wait this long for the network.
export const ONLINE_WAIT_MS = 10000;

// Returns true while the prompt should be held back: a reconnect is scheduled, waiting for the
// network, or in flight.
export default function useReconnectOnReturn(isStale, reconnect, enabled = true) {
  const [returnedAt, setReturnedAt] = useState(null);
  const [triedFor, setTriedFor] = useState(null);
  const [holding, setHolding] = useState(false);
  const [waitingOnline, setWaitingOnline] = useState(false);

  useEffect(() => {
    if (!enabled) return undefined;
    const onShow = () => {
      if (document.visibilityState === 'visible') setReturnedAt(Date.now());
    };
    const onPageShow = (e) => { if (e.persisted) onShow(); };
    document.addEventListener('visibilitychange', onShow);
    window.addEventListener('pageshow', onPageShow);
    return () => {
      document.removeEventListener('visibilitychange', onShow);
      window.removeEventListener('pageshow', onPageShow);
    };
  }, [enabled]);

  const due = enabled && isStale && returnedAt !== null && triedFor !== returnedAt
    && Date.now() - returnedAt < RETURN_WINDOW_MS;

  useEffect(() => {
    if (!due) return undefined;
    const t = setTimeout(() => {
      setTriedFor(returnedAt);
      if (navigator.onLine === false) { setWaitingOnline(true); return; }
      reconnect();
      setHolding(true);
    }, RECONNECT_DELAY_MS);
    return () => clearTimeout(t);
  }, [due, returnedAt]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!waitingOnline) return undefined;
    if (!isStale) { setWaitingOnline(false); return undefined; }
    const onOnline = () => {
      setWaitingOnline(false);
      reconnect();
      setHolding(true);
    };
    window.addEventListener('online', onOnline);
    const t = setTimeout(() => setWaitingOnline(false), ONLINE_WAIT_MS);
    return () => { window.removeEventListener('online', onOnline); clearTimeout(t); };
  }, [waitingOnline, isStale]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!holding) return undefined;
    if (!isStale) { setHolding(false); return undefined; }
    const t = setTimeout(() => setHolding(false), HOLD_MS);
    return () => clearTimeout(t);
  }, [holding, isStale]);

  return due || ((waitingOnline || holding) && isStale);
}
